import Phaser from "phaser";
import ImageHelpers from "./ImageHelpers";

export default class PauseButton {
  private scene: Phaser.Scene;
  private pauseSceneKey: string;
  private parentSceneKey: string;
  private mPauseButton: Phaser.GameObjects.Image;

  private static readonly TEXTURE_KEY = "pauseButton";

  /**
   * Call this from your Scene.preload()
   */
  public static preload(scene: Phaser.Scene): void {
    scene.load.image(PauseButton.TEXTURE_KEY, "images/pauseButton.png");
  }

  /**
   * @param scene           The scene this button lives in (gameplay scene)
   * @param pauseSceneKey   Key of the pause UI scene to launch (default: "PauseScene")
   * @param x               Optional X (defaults to top-left)
   * @param y               Optional Y (defaults to top-left)
   */
  constructor(
    scene: Phaser.Scene,
    pauseSceneKey: string = "PauseScene",
    x?: number,
    y?: number,
  ) {
    this.scene = scene;
    this.pauseSceneKey = pauseSceneKey;
    this.parentSceneKey = scene.scene.key;

    // Default: top-left corner
    const defaultX = x ?? scene.sys.game.scale.gameSize.width - 128;
    const defaultY = y ?? 0;

    this.mPauseButton = scene.add
      .image(defaultX, defaultY, PauseButton.TEXTURE_KEY)
      .setOrigin(0, 0) // anchor to top-left
      .setInteractive({ useHandCursor: true });

    this.mPauseButton.on("pointerup", () => this.pause());

    ImageHelpers.SetHoverEffectOnImage(scene, this.mPauseButton);
  }

  /** Pause gameplay scene + launch pause UI */
  public pause(): void {
    // Add blur to gameplay camera (if available)
    const cam: any = this.scene.cameras.main as any;

    try {
      // Prefer per-effect destroy so we don't wipe other effects
      this.scene.data.set("__pauseBlur", cam?.postFX?.addBlur?.(0, 4, 4, 1));
    } catch {
      // ignore if postFX not supported
    }

    this.scene.scene.pause(this.parentSceneKey);
    this.scene.scene.launch(this.pauseSceneKey, {
      parent: this.parentSceneKey,
    });
    this.scene.scene.bringToTop(this.pauseSceneKey);
  }

  /** Change button position at runtime */
  public setPosition(x: number, y: number): this {
    this.mPauseButton.setPosition(x, y);
    return this;
  }

  /** Access to the underlying image if you need extra tweaks */
  public get image(): Phaser.GameObjects.Image {
    return this.mPauseButton;
  }

  /** Destroy the button cleanly */
  public destroy(): void {
    this.mPauseButton.removeAllListeners();
    this.mPauseButton.destroy();
  }
}
